/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { lazy, Suspense } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';

import { useAtlassianUserContext } from '../../../hooks/use-user-context';

const LazyDebugPanelComponent = lazy(() =>
	import(/* webpackChunkName: "@atlassian/twg-microsite-debug-panel" */ './Component').then(
		(module) => ({
			default: module.DebugPanel,
		}),
	),
);

/**
 * Renders `<DebugPanel>` only when the `twg_microsite_debug_panel` feature
 * gate is on. When the gate is off this returns `null` synchronously, which
 * means the lazy import is never evaluated and the corresponding JS chunk is
 * never requested by the browser.
 *
 * Subscribes to `UserContext` so the component re-renders after login
 * completes and feature gates are initialized (otherwise `fg()` would
 * only be evaluated on the initial mount when gates are still unpatched).
 */
export const DebugPanel = (): JSX.Element | null => {
	void useAtlassianUserContext();

	if (!fg('twg_microsite_debug_panel')) {
		return null;
	}

	return (
		<Suspense fallback={null}>
			<LazyDebugPanelComponent />
		</Suspense>
	);
};
